import { useEffect, useState } from 'react';
import { Toaster, toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';
import CrudModal from '../../components/admin/CrudModal';
import { adminDelete, adminGet, adminPost, adminPut } from '../../lib/adminApi';

const emptyInvoice = {
  reference: '',
  client: '',
  intervention_reference: '',
  amount: 0,
  status: 'a_valider',
  issue_date: '',
  due_date: '',
  notes: '',
};

const AdminFacturation = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formState, setFormState] = useState(emptyInvoice);
  const [submitting, setSubmitting] = useState(false);

  const fetchData = async () => {
    try {
      setItems(await adminGet('/admin/invoices'));
    } catch (error) {
      console.error('Error fetching invoices:', error);
      toast.error('Erreur lors du chargement des factures');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openCreateModal = () => {
    setEditingItem(null);
    setFormState(emptyInvoice);
    setModalOpen(true);
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setFormState({ ...emptyInvoice, ...item });
    setModalOpen(true);
  };

  const handleChange = (field, value) => {
    setFormState((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...formState, amount: Number(formState.amount || 0) };
      if (editingItem) {
        await adminPut(`/admin/invoices/${editingItem.id}`, payload);
        toast.success('Facture mise à jour');
      } else {
        await adminPost('/admin/invoices', payload);
        toast.success('Facture créée');
      }
      setModalOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error saving invoice:', error);
      toast.error('Erreur lors de l’enregistrement');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Supprimer cette facture ?')) return;
    try {
      await adminDelete(`/admin/invoices/${itemId}`);
      toast.success('Facture supprimée');
      fetchData();
    } catch (error) {
      console.error('Error deleting invoice:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="crm-loading">
          <div className="crm-spinner" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout onAction={openCreateModal}>
      <Toaster position="top-right" richColors />
      <section className="crm-card">
        <div className="crm-section-head">
          <div>
            <h2 className="crm-section-title">Facturation</h2>
            <p className="crm-section-subtitle">
              Créez et suivez les factures liées aux interventions.
            </p>
          </div>
        </div>

        <div className="crm-table-wrap">
          <table className="crm-table">
            <thead>
              <tr>
                <th>Facture</th>
                <th>Client</th>
                <th>Montant</th>
                <th>Statut</th>
                <th>Émission</th>
                <th>Échéance</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan="7"><div className="crm-empty">Aucune facture enregistrée.</div></td>
                </tr>
              ) : (
                items.map((item) => (
                  <tr key={item.id}>
                    <td>{item.reference}</td>
                    <td>{item.client}</td>
                    <td>{Math.round(Number(item.amount || 0)).toLocaleString('fr-FR')} €</td>
                    <td>{item.status}</td>
                    <td>{item.issue_date}</td>
                    <td>{item.due_date}</td>
                    <td>
                      <div className="crm-table__actions">
                        <button type="button" className="crm-action-link" onClick={() => openEditModal(item)}>Modifier</button>
                        <button type="button" className="crm-action-danger" onClick={() => handleDelete(item.id)}>Supprimer</button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {modalOpen ? (
        <CrudModal
          title={editingItem ? 'Modifier une facture' : 'Créer une facture'}
          onClose={() => setModalOpen(false)}
          onSubmit={handleSubmit}
          submitting={submitting}
          submitLabel={editingItem ? 'Mettre à jour' : 'Créer'}
        >
          <div className="crm-form__grid">
            <div className="crm-form__field">
              <label>Référence</label>
              <input value={formState.reference} onChange={(e) => handleChange('reference', e.target.value)} required />
            </div>
            <div className="crm-form__field">
              <label>Client</label>
              <input value={formState.client} onChange={(e) => handleChange('client', e.target.value)} required />
            </div>
            <div className="crm-form__field">
              <label>Réf. intervention</label>
              <input value={formState.intervention_reference} onChange={(e) => handleChange('intervention_reference', e.target.value)} />
            </div>
            <div className="crm-form__field">
              <label>Montant</label>
              <input type="number" min="0" value={formState.amount} onChange={(e) => handleChange('amount', e.target.value)} />
            </div>
            <div className="crm-form__field">
              <label>Statut</label>
              <select value={formState.status} onChange={(e) => handleChange('status', e.target.value)}>
                <option value="a_valider">À valider</option>
                <option value="emise">Émise</option>
                <option value="payee">Payée</option>
                <option value="annulee">Annulée</option>
              </select>
            </div>
            <div className="crm-form__field">
              <label>Date d'émission</label>
              <input type="date" value={formState.issue_date} onChange={(e) => handleChange('issue_date', e.target.value)} required />
            </div>
            <div className="crm-form__field">
              <label>Date d'échéance</label>
              <input type="date" value={formState.due_date} onChange={(e) => handleChange('due_date', e.target.value)} required />
            </div>
            <div className="crm-form__field crm-form__field--full">
              <label>Notes</label>
              <textarea value={formState.notes} onChange={(e) => handleChange('notes', e.target.value)} />
            </div>
          </div>
        </CrudModal>
      ) : null}
    </AdminLayout>
  );
};

export default AdminFacturation;
